// © 2013 - 2024 Tinder, Inc., ALL RIGHTS RESERVED

import { css } from '@linaria/core';

import { themeCssVar as globalTheme } from '../../utils/theme';

export const theme = {
  // Container
  backgroundContainer: globalTheme.color.backgroundBannerDefault,
  containerPaddingTop: globalTheme.sizing.paddingLarge,
  containerPaddingHorizontal: globalTheme.sizing.paddingLarge,
  containerPaddingBottom: globalTheme.sizing.paddingXlarge,
  heroPaddingVertical: globalTheme.sizing.paddingXxlarge,
  contentPaddingGap: globalTheme.sizing.paddingMedium,
  heroContentPaddingGap: globalTheme.sizing.paddingLarge,
  // Feature Icon
  backgroundIconContainer: globalTheme.gradient.iconContainerBannerDefault,
  featureIconSize: globalTheme.sizing.iconMedium,
  featureIconContainerSize: globalTheme.sizing.dimensionLarge,
  // Text Headline
  foregroundHeadline: globalTheme.color.textBannerHeading,
  fontFamilyHeadline: globalTheme.fontFamily.subheading1,
  fontSizeHeadline: globalTheme.fontSize.subheading1,
  fontWeightHeadline: globalTheme.fontWeight.subheading1,
  lineHeightHeadline: globalTheme.lineHeight.subheading1,
  letterSpacingHeadline: globalTheme.letterSpacing.subheading1,
  textTransformHeadline: globalTheme.letterCasing.subheading1,
  headlinePaddingGap: globalTheme.sizing.paddingSmall,
  headingLineClamp: 2,
  // Text Body
  foregroundBody: globalTheme.color.textBannerBody,
  fontFamilyBody: globalTheme.fontFamily.body2Regular,
  fontSizeBody: globalTheme.fontSize.body2Regular,
  fontWeightBody: globalTheme.fontWeight.body2Regular,
  lineHeightBody: globalTheme.lineHeight.body2Regular,
  letterSpacingBody: globalTheme.letterSpacing.body2Regular,
  textTransformBody: globalTheme.letterCasing.body2Regular,
  paddingTextGap: globalTheme.sizing.paddingXxsmall,
  bodyLineClmap: 3,
  // Dismiss Icon
  foregroundDismissIcon: globalTheme.color.iconBannerDismiss,
  dismissIconSize: globalTheme.sizing.iconXsmall,
  // Button
  paddingButtonGap: globalTheme.sizing.paddingLarge,
  // Divider
  divider: globalTheme.color.dividerBannerDefault,
};

const cssVariable = {
  backgroundContainer: `--tui-banner-background-container`,
  backgroundIconContainer: `--tui-banner-background-icon-container`,
  foregroundHeadline: `--tui-banner-foreground-headline`,
  foregroundBody: `--tui-banner-foreground-body`,
  bodyLineClmap: `--tui-banner-body-line-clamp`,
  headingLineClamp: `--tui-banner-heading-line-clamp`,
};

const container = css`
  /* Block */
  display: flex;
  position: absolute;
  border: 0;
  top: 0
  left: 0;
  width: 100%;
  background: var(${cssVariable.backgroundContainer});
  border-bottom: 1px solid ${theme.divider};

  /* Variables */
  ${cssVariable.backgroundContainer}: ${theme.backgroundContainer};
  ${cssVariable.backgroundIconContainer}: ${theme.backgroundIconContainer};
  ${cssVariable.foregroundHeadline}: ${theme.foregroundHeadline};
  ${cssVariable.foregroundBody}: ${theme.foregroundBody};
  ${cssVariable.bodyLineClmap}: ${theme.bodyLineClmap};
  ${cssVariable.headingLineClamp}: ${theme.headingLineClamp};

  &[data-hero] {
    justify-content: center;
    align-items: center;
  }
`;

const content = css`
  display: flex;
  width: 100%;
  position: relative;
  gap: ${theme.contentPaddingGap};
  background: var(${cssVariable.backgroundContainer});
  padding: ${theme.containerPaddingTop} ${theme.containerPaddingHorizontal}
    ${theme.containerPaddingBottom};

  .${container}[data-hero] & {
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${theme.heroContentPaddingGap};
    padding: ${theme.heroPaddingVertical} ${theme.containerPaddingHorizontal};
    max-width: 960px;
  }
`;

const verticalContent = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${theme.contentPaddingGap};

  .${container}[data-hero] & {
    justify-content: center;
    align-items: center;
  }
`;

const dismissContainer = css`
  position: absolute;
  top: ${theme.containerPaddingTop};
  right: ${theme.containerPaddingHorizontal};
  padding: ${theme.containerPaddingHorizontal};
  margin: calc(${theme.containerPaddingHorizontal} * -1);
`;

const dismissIcon = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${theme.dismissIconSize};
  height: ${theme.dismissIconSize};
  cursor: pointer;
`;
const textContainer = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.paddingTextGap};

  .${container}[data-hero] & {
    text-align: center;
    justify-content: center;
    align-items: center;
  }
`;

const headlineContainer = css`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-right: calc(${theme.dismissIconSize} + ${theme.headlinePaddingGap});

  .${container}[data-hero] & {
    text-align: center;
    justify-content: center;
    align-items: center;
  }
`;

const title = css`
  display: -webkit-box;
  -webkit-line-clamp: var(${cssVariable.headingLineClamp});
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  position: relative;
  font-family: ${theme.fontFamilyHeadline};
  font-size: ${theme.fontSizeHeadline};
  font-weight: ${theme.fontWeightHeadline};
  line-height: ${theme.lineHeightHeadline};
  letter-spacing: ${theme.letterSpacingHeadline};
  text-transform: ${theme.textTransformHeadline};
  color: var(${cssVariable.foregroundHeadline});
`;

const body = css`
  display: -webkit-box;
  -webkit-line-clamp: var(${cssVariable.bodyLineClmap});
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  position: relative;
  font-family: ${theme.fontFamilyBody};
  font-size: ${theme.fontSizeBody};
  font-weight: ${theme.fontWeightBody};
  line-height: ${theme.lineHeightBody};
  letter-spacing: ${theme.letterSpacingBody};
  text-transform: ${theme.textTransformBody};
  color: var(${cssVariable.foregroundBody});
`;

const action = css`
  display: flex;
`;

const featureIconContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: var(${cssVariable.backgroundIconContainer});
  border-radius: 50%;
  width: ${theme.featureIconContainerSize};
  height: ${theme.featureIconContainerSize};
`;

const featureIcon = css`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: ${theme.featureIconSize};
  height: ${theme.featureIconSize};
`;

const styles = {
  container,
  content,
  verticalContent,
  headlineContainer,
  textContainer,
  title,
  body,
  action,
  featureIconContainer,
  featureIcon,
  dismissContainer,
  dismissIcon,
};

export default styles;
